import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import {
  HttpErrorResponse,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { PromiseResult } from '../models/PromiseResult';
import { RestApi } from '../shared/apiUrls';
import { catchError } from 'rxjs/operators';
import {
  SupporCreateUser,
  VendorFetchFilter,
  ContactUser,
  DownloadCsvData,
  consent_update
} from '../models/supportcreateuser';
import {
  PackageConsultDoctorApiData,
  SlotsFetchApiData,
  SupportConsultDoctorVerifyApi,
  consultApiData
} from '../models/consultdoctors';

@Injectable({
  providedIn: 'root'
})
export class SupportService {
  constructor(private readonly http: HttpClient) { }
  private handleError(error: HttpErrorResponse) {
    return throwError(error);
  }

  consultDoctor(consultation: consultApiData): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'consultation/create',
        consultation
      )
      .pipe(catchError(this.handleError));
  }

  verifyPatientRequest(
    data: SupportConsultDoctorVerifyApi
  ): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + '/support/verify/patient/request',
        data
      )
      .pipe(catchError(this.handleError));
  }

  UploadFiles(data: FormData): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'patient/file/upload',
        data
      )
      .pipe(catchError(this.handleError));
  }

  DeleteFiles(data): Observable<any> {
    return this.http
      .delete<PromiseResult>(
        RestApi.Urls.SupportApi + 'patient/file/delete',
        data
      )
      .pipe(catchError(this.handleError));
  }

  DeleteFiles1(data): Observable<any> {
    let params = new HttpParams();
    params = params
      .append('patient_guid', data.patient_guid)
      .append('document_key', data.document_key);
    return this.http
      .delete<PromiseResult>(RestApi.Urls.SupportApi + 'patient/file/delete', {
        params
      })
      .pipe(catchError(this.handleError));
  }

  GetDocuments(
    patient_guid: string,
    query?: Record<string, string | number | boolean>
  ): Observable<PromiseResult> {
    let params = new HttpParams();
    Object.entries(query).forEach(
      ([key, value]) => (params = params.append(key, value.toString()))
    );
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.SupportApi + 'patient/file/findAll/' + patient_guid,
        { params }
      )
      .pipe(catchError(this.handleError));
  }

  getPriorityByGuid(
    patient_guid: string,
    consultation_type_id: number,
    specialty_id?: number
  ): Observable<PromiseResult> {
    let params = new HttpParams();
    params = params
      .append('patient_guid', patient_guid)
      .append('consultation_type_id', consultation_type_id.toString());
    if (specialty_id != null)
      params = params.append('specialty_id', specialty_id.toString());
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.SupportApi + 'consultation/priority/fetch',
        { params: params }
      )
      .pipe(catchError(this.handleError));
  }

  findPackages(data: PackageConsultDoctorApiData): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'consultation/packages/find',
        data
      )
      .pipe(catchError(this.handleError));
  }

  downloadCsv(data: DownloadCsvData) {
    return this.http
      .post(RestApi.Urls.SupportApi + 'vendor/request/export/csv', data, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(catchError(this.handleError));
  }

  createUser(user: SupporCreateUser): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'vendor/request/create',
        user
      )
      .pipe(catchError(this.handleError));
  }

  fetchVendorList(): Observable<PromiseResult> {
    return this.http
      .get<PromiseResult>(RestApi.Urls.VendorsApi + 'get/all')
      .pipe(catchError(this.handleError));
  }

  fetchStatusList(): Observable<PromiseResult> {
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.SupportApi + 'vendor/request/status/fetch'
      )
      .pipe(catchError(this.handleError));
  }

  fetchPateintDetails(req_id: number): Observable<PromiseResult> {
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.SupportApi + 'vendor/request/view/detail/' + req_id
      )
      .pipe(catchError(this.handleError));
  }

  fetchRequestActions(req_id: number): Observable<PromiseResult> {
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.SupportApi +
        'support/request/activity/' +
        req_id +
        '/getAll'
      )
      .pipe(catchError(this.handleError));
  }

  ContactUser(user: ContactUser): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'support/contact/patient/create',
        user
      )
      .pipe(catchError(this.handleError));
  }

  GetVendorPatients(body: VendorFetchFilter): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'vendor/request/fetch',
        body
      )
      .pipe(catchError(this.handleError));
  }

  getSlotsForConsultation(data: SlotsFetchApiData): Observable<PromiseResult> {
    return this.http
      .post<PromiseResult>(
        RestApi.Urls.SupportApi + 'consultation/slots-vendor',
        data
      )
      .pipe(catchError(this.handleError));
  }

  getDietPreference(): Observable<PromiseResult> {
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.PatientApi + 'consultation/diet/fetch/types'
      )
      .pipe(catchError(this.handleError));
  }

  fetchConsultationTypes(vendor_guid: string): Observable<PromiseResult> {
    return this.http
      .get<PromiseResult>(
        RestApi.Urls.SupportApi +
        'vendor/' +
        vendor_guid +
        '/fetch/consultations'
      )
      .pipe(catchError(this.handleError));
  }

  UpdateConsent(data: consent_update): Observable<PromiseResult> {
    return this.http.patch<PromiseResult>(RestApi.Urls.SupportApi + "request/update/consent", data).pipe(catchError(this.handleError));
  }
}
